// @ts-nocheck
import { fabric } from "fabric";
import { v4 as uuid4 } from "uuid";

import {
  CanvasMouseDown,
  CanvasMouseMove,
  CanvasMouseUp,
  CanvasObjectModified,
  CanvasObjectScaling,
  CanvasPathCreated,
  CanvasSelectionCreated,
  CustomFabricObject,
} from "../types/type";
import { TO_JSON_ADDITIONAL_PROPERTIES, defaultNavElement } from "../constants";
import { createSpecificShape } from "./shapes";
import { CurvedText } from "./curved-text";
import { getLayerNameByType } from "./utils";

/////////////////////////////
// OVERRIDE FROM FABRIC.JS //
/////////////////////////////
fabric.Object.NUM_FRACTION_DIGITS = 9; // Override fabric number of fraction digits
///////////////////////////////////////////

// initialize fabric canvas
export const initializeFabric = ({
  fabricRef,
  canvasRef,
  dimensions,
  isEditMode,
  templateData,
  suppressHistoryRef,
}: {
  fabricRef: React.MutableRefObject<fabric.Canvas | null>;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  dimensions: {
    width: number;
    height: number;
  };
  isEditMode: boolean;
  templateData: any;
  suppressHistoryRef: React.MutableRefObject<boolean>;
}) => {
  // suppress history saving if needed.
  if (suppressHistoryRef) {
    suppressHistoryRef.current = true; // suppress history saving until the canvas is initialized.
  }

  const dimensionsToRender = {} as any;

  // if we are in edit mode, take the dimensions from the templateData
  if (isEditMode) {
    dimensionsToRender.width = templateData?.canvasDimensions?.width;
    dimensionsToRender.height = templateData?.canvasDimensions?.height;
  } else {
    dimensionsToRender.width = dimensions.width;
    dimensionsToRender.height = dimensions.height;
  }

  // create fabric canvas
  const canvas = new fabric.Canvas(canvasRef.current, {
    width: dimensionsToRender.width,
    height: dimensionsToRender.height,
    selectionKey: "ctrlKey",
  });

  // set canvas reference to fabricRef so we can use it later anywhere outside canvas listener
  fabricRef.current = canvas;

  if (isEditMode) {
    loadCanvasDataComingFromJSON(canvas, templateData.canvas, () => {
      suppressHistoryRef.current = false; // ✅ un-suppress *after* objects are loaded
    });
  } else {
    suppressHistoryRef.current = false; // create mode, nothing async to wait
  }

  return canvas;
};

// instantiate creation of custom fabric object/shape and add it to canvas
export const handleCanvasMouseDown = ({
  options,
  canvas,
  selectedShapeRef,
  isDrawing,
  shapeRef,
}: CanvasMouseDown) => {
  // get pointer coordinates
  const pointer = canvas.getPointer(options.e);

  /**
   * get target object i.e., the object that is clicked
   * findTarget() returns the object that is clicked
   *
   * findTarget: http://fabricjs.com/docs/fabric.Canvas.html#findTarget
   */
  const target = canvas.findTarget(options.e, false);

  // set canvas drawing mode to false
  canvas.isDrawingMode = false;

  // if selected shape is freeform, set drawing mode to true and return
  if (selectedShapeRef.current === "freeform") {
    isDrawing.current = true;
    canvas.isDrawingMode = true;
    canvas.freeDrawingBrush.width = 5;
    return;
  }

  canvas.isDrawingMode = false;

  // if target is the selected shape or active selection, set isDrawing to false
  if (
    target &&
    (target.type === selectedShapeRef.current ||
      target.type === "activeSelection")
  ) {
    isDrawing.current = false;

    // set active object to target
    canvas.setActiveObject(target);

    /**
     * setCoords() is used to update the controls of the object
     * setCoords: http://fabricjs.com/docs/fabric.Object.html#setCoords
     */
    target.setCoords();
  } else {
    isDrawing.current = true;

    // create custom fabric object/shape and set it to shapeRef
    shapeRef.current = createSpecificShape(
      selectedShapeRef.current,
      pointer as any
    );

    // if shapeRef is not null, add it to canvas
    if (shapeRef.current) {
      // add: http://fabricjs.com/docs/fabric.Canvas.html#add
      canvas.add(shapeRef.current);
    }
  }
};

// handle mouse move event on canvas to draw shapes with different dimensions
export const handleCanvasMouseMove = ({
  options,
  canvas,
  isDrawing,
  selectedShapeRef,
  shapeRef,
  syncShapeInStorage,
}: CanvasMouseMove) => {
  // if selected shape is freeform, return
  if (!isDrawing.current) return;
  if (selectedShapeRef.current === "freeform") return;

  canvas.isDrawingMode = false;

  // get pointer coordinates
  const pointer = canvas.getPointer(options.e);

  // depending on the selected shape, set the dimensions of the shape stored in shapeRef in previous step of handelCanvasMouseDown
  // calculate shape dimensions based on pointer coordinates
  switch (selectedShapeRef?.current) {
    case "rectangle":
      shapeRef.current?.set({
        width: pointer.x - (shapeRef.current?.left || 0),
        height: pointer.y - (shapeRef.current?.top || 0),
      });
      break;

    case "circle":
      shapeRef.current.set({
        radius: Math.abs(pointer.x - (shapeRef.current?.left || 0)) / 2,
      });
      break;

    case "triangle":
      shapeRef.current?.set({
        width: pointer.x - (shapeRef.current?.left || 0),
        height: pointer.y - (shapeRef.current?.top || 0),
      });
      break;

    case "line":
      shapeRef.current?.set({
        x2: pointer.x,
        y2: pointer.y,
      });
      break;

    case "image":
      shapeRef.current?.set({
        width: pointer.x - (shapeRef.current?.left || 0),
        height: pointer.y - (shapeRef.current?.top || 0),
      });
      break;

    default:
      break;
  }

  // render objects on canvas
  // renderAll: http://fabricjs.com/docs/fabric.Canvas.html#renderAll
  canvas.renderAll();

  // sync shape in storage
  if (shapeRef.current?.objectId) {
    syncShapeInStorage(shapeRef.current);
  }
};

// handle mouse up event on canvas to stop drawing shapes
export const handleCanvasMouseUp = ({
  canvas,
  isDrawing,
  shapeRef,
  activeObjectRef,
  selectedShapeRef,
  syncShapeInStorage,
  setActiveElement,
}: CanvasMouseUp) => {
  isDrawing.current = false;
  if (selectedShapeRef.current === "freeform") return;

  // sync shape in storage as drawing is stopped
  syncShapeInStorage(shapeRef.current);

  // set everything to null
  shapeRef.current = null;
  activeObjectRef.current = null;
  selectedShapeRef.current = null;

  // if canvas is not in drawing mode, set active element to default nav element after 700ms
  if (!canvas.isDrawingMode) {
    setTimeout(() => {
      setActiveElement(defaultNavElement);
    }, 700);
  }
};

// update shape in storage when object is modified
export const handleCanvasObjectModified = ({
  options,
  syncShapeInStorage,
}: CanvasObjectModified) => {
  const target = options.target;
  if (!target) return;

  if (target?.type == "activeSelection") {
    // fix this
  } else {
    syncShapeInStorage(target);
  }
};

// update shape in storage when path is created when in freeform mode
export const handlePathCreated = ({
  options,
  syncShapeInStorage,
}: CanvasPathCreated) => {
  // get path object
  const path = options.path;
  if (!path) return;

  // set unique id to path object
  path.set({
    objectId: uuid4(),
  });

  // sync shape in storage
  syncShapeInStorage(path);
};

// check how object is moving on canvas and restrict it to canvas boundaries
export const handleCanvasObjectMoving = ({
  options,
}: {
  options: fabric.IEvent;
}) => {
  // get target object which is moving
  const target = options.target as fabric.Object;

  // target.canvas is the canvas on which the object is moving
  const canvas = target.canvas as fabric.Canvas;

  // set coordinates of target object
  target.setCoords();

  // restrict object to canvas boundaries (horizontal)
  if (target && target.left) {
    target.left = Math.max(
      0,
      Math.min(
        target.left,
        (canvas.width || 0) - (target.getScaledWidth() || target.width || 0)
      )
    );
  }

  // restrict object to canvas boundaries (vertical)
  if (target && target.top) {
    target.top = Math.max(
      0,
      Math.min(
        target.top,
        (canvas.height || 0) - (target.getScaledHeight() || target.height || 0)
      )
    );
  }
};

// set element attributes when element is selected
export const handleCanvasSelectionCreated = ({
  options,
  setElementAttributes,
}: CanvasSelectionCreated) => {
  console.log("selection created", options);
  // if no element is selected, return
  if (!options?.selected) return;
  if (options.selected.length === 0) return; // if non selected, return.

  // if only one element is selected, set element attributes.
  // could be group or single option, attributes should handle that.
  if (options.selected.length === 1) {
    const elementAttributes = getElementAttributeForSingleObject(
      options.selected[0] as CustomFabricObject<any>
    );

    setElementAttributes(elementAttributes);
  } else {
    // if multiple elements are selected, set element attributes for active selection
    // only for active selection, group will be always like single object, with
    // _objects inside it.
    const elementAttributes = getElementsAttributeForActiveSelection(
      options.selected as fabric.Object[]
    );

    setElementAttributes(elementAttributes);
  }
};

// When selection is updated from one item to another, this will be called.
// updated the element attributes.
export const handleCanvasSelectionUpdated = ({
  canvas,
  options,
  setElementAttributes,
}: any) => {
  console.log("selection updated", options);
  console.log(
    " selection updated canvas.getActiveObject()",
    canvas.getActiveObject()
  );

  // selection update have selected/deselected arrays. Not always populated.
  // 1- if we swap from one object to another, then both will be populated.
  // 2- if we use incremental selection with shift key, then only the new selected
  // item is populated. The current one is not populated in the options.
  // 3- if we incrementally deselect with shift key, then only the deselected item
  // is populated.

  // most probably the best way, is to always call canvas.getActiveObject() to get
  // the latest and most accurate selected object / active selection.

  const activeObject = canvas.getActiveObject();

  if (!activeObject) return;

  const isActiveSelection = activeObject?.type === "activeSelection";

  if (isActiveSelection) {
    // if active selection, then we need to get the objects inside the active selection.
    // then we can get the attributes of the active selection.
    const elementAttributes = getElementsAttributeForActiveSelection(
      activeObject.getObjects() as fabric.Object[]
    );

    setElementAttributes(elementAttributes);
  } else {
    // can be a group or single object, all will be handled here.
    const elementAttributes = getElementAttributeForSingleObject(
      activeObject as CustomFabricObject<any>
    );

    setElementAttributes(elementAttributes);
  }
};

// when selection is cleared, set element attributes to default.
export const handleCanvasSelectionCleared = ({
  options,
  setElementAttributes,
}: any) => {
  console.log("selection cleared", options);
  setElementAttributes(getDefaultElementAttributes());
};

// this is when state is empty. Cleared selection.
export const getDefaultElementAttributes = () => {
  return {
    width: 0, // NUMBER
    height: 0, // NUMBER
    left: 0, // NUMBER
    top: 0, // NUMBER
    objectId: null,
    type: null,
    consumerLocks: null,
    fill: "",
    stroke: "",
    strokeWidth: 0, // NUMBER
    angle: 0, // NUMBER
    flipX: false,
    flipY: false,
  };
};

export const getElementAttributeForSingleObject = (
  selectedElement: CustomFabricObject<any>
) => {
  // Get the bounding box that accounts for rotation and scaling
  const boundingBox = selectedElement.getBoundingRect(true); // `true` means accounting for transformations

  // base case, shape or image, and base for texts.
  const elementAttributes = {
    width: parseInt(Math.round(boundingBox.width)), // Accurate width
    height: parseInt(Math.round(boundingBox.height)), // Accurate height
    left: parseInt(Math.round(boundingBox.left)), // Accurate left position
    top: parseInt(Math.round(boundingBox.top)), // Accurate top position

    objectId: selectedElement.objectId,
    type: selectedElement?.type,

    consumerLocks: selectedElement?.consumerLocks,

    layerName: selectedElement?.layerName,

    angle: parseInt(Math.round(selectedElement?.angle)), // NUMBER
    flipX: selectedElement?.flipX,
    flipY: selectedElement?.flipY,
  };

  // if not group, add fill, stroke, strokeWidth. Active selection don't come here.
  // any other case than group would need these properties.
  if (selectedElement.type !== "group") {
    elementAttributes.fill = selectedElement.fill;
    elementAttributes.stroke = selectedElement.stroke;
    elementAttributes.strokeWidth = selectedElement.strokeWidth;
  }

  if (selectedElement.type === "i-text") {
    elementAttributes.text = selectedElement.text;
    elementAttributes.fontFamily = selectedElement.fontFamily;
    elementAttributes.fontSize = selectedElement.fontSize;
    elementAttributes.fontWeight = selectedElement.fontWeight;
  }

  // curved text case.
  if (selectedElement.type === "i-text" && selectedElement.isCurvedText) {
    // curved text properties
    elementAttributes.isCurvedText = selectedElement.isCurvedText;
    elementAttributes.diameter = selectedElement.diameter;
    elementAttributes.arcAngle = selectedElement.arcAngle;
    elementAttributes.startAngle = selectedElement.startAngle;
  }

  // group case, add childObjectIds if needed in future.
  if (selectedElement.type === "group") {
    // group properties
    elementAttributes.childObjectIds = selectedElement._objects.map(
      (object) => object.objectId
    );
  }

  return elementAttributes;
};

const getElementsAttributeForActiveSelection = (
  selectedElements: fabric.Object[]
) => {
  // this will get the attributes when user select multiple objects. (active selection)

  const elementAttributes = {
    width: 0, // NUMBER
    height: 0, // NUMBER
    left: 0, // NUMBER
    top: 0, // NUMBER

    objectId: null, // this will be null for active selection
    objectIds: selectedElements?.map((element) => element.objectId), // this will be array of object ids for active selection
    type: "activeSelection",

    consumerLocks: null,
    layerName: getLayerNameByType("activeSelection"), // this just to populate the UI, not gonna be able to change it.

    fill: "",
    stroke: "",
    strokeWidth: 0, // NUMBER

    angle: 0,
  };

  return elementAttributes;
};

// when clicking on the left side bar, we need to select the object on the canvas.
export const handleManualObjectSelection = ({ canvas, objectId }) => {
  if (!canvas) return;

  // objects if they belong inside groups, they should not be selected.
  // sidebar updates, will not be able to modify objects inside groups,
  // as fabric group is made to represent and restrict objects inside it.

  // get object by id
  const targetObject = canvas?.getObjects().find((object) => {
    return object.objectId === objectId;
  });

  // if object is found, set it as active object
  if (targetObject) {
    canvas.discardActiveObject();
    canvas.setActiveObject(targetObject);
  }

  // render canvas
  canvas.requestRenderAll();

  // this will fire selection updated, then it should update the element attributes. //ONCE IMPLEMENTED.
};

export const handleManualSelectAllObjects = ({ canvas }) => {
  if (!canvas) return;
  // if canvas is empty, return
  if (canvas.getObjects().length === 0) return;

  canvas.discardActiveObject();
  canvas.requestRenderAll();

  canvas.setActiveObject(
    new fabric.ActiveSelection(canvas.getObjects(), {
      canvas: canvas,
    })
  );

  canvas.requestRenderAll();
};

// update element attributes when element is scaled
export const handleCanvasObjectScaling = ({
  options,
  setElementAttributes,
}: CanvasObjectScaling) => {
  const selectedElement = options.target;

  // active selection has no dimensions control.
  if (!selectedElement || selectedElement.type === "activeSelection") return;

  // calculate scaled dimensions of the object
  const scaledWidth = selectedElement?.scaleX
    ? selectedElement?.width! * selectedElement?.scaleX
    : selectedElement?.width;

  const scaledHeight = selectedElement?.scaleY
    ? selectedElement?.height! * selectedElement?.scaleY
    : selectedElement?.height;

  setElementAttributes((prev) => ({
    ...prev,
    width: parseInt(scaledWidth),
    height: parseInt(scaledHeight),
  }));
};

// update element attributes when element is rotated.
export const handleCanvasObjectRotating = ({
  options,
  setElementAttributes,
}: CanvasObjectScaling) => {
  const selectedElement = options.target;

  // active selection has no dimensions control.
  if (!selectedElement || selectedElement.type === "activeSelection") return;

  // calculate scaled dimensions of the object
  const scaledWidth = selectedElement?.scaleX
    ? selectedElement?.width! * selectedElement?.scaleX
    : selectedElement?.width;

  const scaledHeight = selectedElement?.scaleY
    ? selectedElement?.height! * selectedElement?.scaleY
    : selectedElement?.height;

  const angle = parseInt(Math.round(selectedElement?.angle)); // NUMBER

  setElementAttributes((prev) => ({
    ...prev,
    angle: angle,
    width: parseInt(scaledWidth),
    height: parseInt(scaledHeight),
  }));
};

// resize canvas dimensions on window resize
export const handleResize = ({
  canvas,
  newDimensions,
}: {
  canvas: fabric.Canvas | null;
  newDimensions: { width: number; height: number };
}) => {
  if (canvas) {
    canvas.setDimensions(newDimensions);
  }
};

// will be used to determine current center of the object, before modifying a text,
// so we can keep the text in center while modifying. // new lock to update text in place.
export function captureCanvasObjectOriginalCenter(object: fabric.Object) {
  if (!object) return null;

  let originalCenter = object.getCenterPoint();
  return originalCenter;
}

// Recenter the text after the text changes to its position before modification.
// this is gonna be the new lock logic. Consumer would need this exact same code as well.
// TEST WITHOUT THE LOCK FOR NOW.
export function recenterTextToOriginalCenter(
  textObj: fabric.Object,
  originalCenter: any,
  canvas: fabric.Canvas
) {
  var newCenter = textObj.getCenterPoint();

  // Adjust position based on the difference from the original center
  var deltaX = originalCenter.x - newCenter.x;
  var deltaY = originalCenter.y - newCenter.y;

  // Reposition the text to maintain the original center
  textObj.set({
    left: textObj.left + deltaX,
    top: textObj.top + deltaY,
  });

  // Update the coordinates and re-render the canvas
  textObj.setCoords();
  canvas.renderAll();
}

export const loadCanvasDataComingFromJSON = (
  canvas,
  canvasInJSON,
  onComplete: any = null
) => {
  canvas.loadFromJSON(canvasInJSON, function () {
    // Function to revive CurvedText recursively.
    // When curved-text is under a group or alone, it has to be revived with the custom,
    // class instance, otherwise it will be revived as a normal text object.

    const reviveCurvedText = (obj) => {
      if (obj.isCurvedText) {
        const { text, ...rest } = obj;

        // Create a new CurvedText instance with the same properties
        const curvedText = new CurvedText(text, {
          ...rest,
        });

        curvedText.updatePath(); // Ensure path is recalculated
        curvedText.setCoords();

        return curvedText;
      }
      return obj; // Return the original object if it's not CurvedText
    };

    const traverseAndRevive = (obj) => {
      if (obj.type === "group" && obj._objects) {
        obj._objects = obj._objects.map(traverseAndRevive); // Recursively revive objects in the group
      }
      return reviveCurvedText(obj); // Revive the current object if needed
    };

    // Loop through all objects in the canvas and revive them
    canvas.forEachObject((obj) => {
      const revivedObj = traverseAndRevive(obj);

      if (revivedObj !== obj) {
        canvas.remove(obj);
        canvas.add(revivedObj);
      }

      revivedObj.setCoords(); // Update coordinates for all objects
    });

    canvas.calcOffset();
    canvas.discardActiveObject(); // Deselect any objects
    canvas.renderAll(); // Re-render the canvas

    // ✅ Call onComplete when everything is loaded and revived
    if (typeof onComplete === "function") {
      onComplete(canvas);
    }
  });
};

export const handleCanvasToJSON = ({
  fabricRef,
}: {
  fabricRef: React.MutableRefObject<fabric.Canvas | null>;
}) => {
  if (!fabricRef.current) return null;

  const json = fabricRef.current.toJSON(TO_JSON_ADDITIONAL_PROPERTIES);

  return json;
};

export const getPngImageFromCanvas = (
  fabricRef: React.MutableRefObject<fabric.Canvas | null>
) => {
  if (!fabricRef.current) return null;

  const dataURL = fabricRef.current.toDataURL({
    format: "png",
    quality: 1, // highest quality
  });

  return dataURL;
};
